import { useState, useMemo, useCallback, useRef, memo, ReactNode } from 'react';
import { Box, List, ListItem, PopoverProps } from '@chakra-ui/react';
import { Button, Checkbox, Colors, Text, Icon } from '@mercator-gcp/grout';
import { useVirtualizer } from '@tanstack/react-virtual';
import PopoverMenu from 'components/elements/PopoverMenu';
import SearchInput from 'components/elements/SearchInput';

const filterOptionsStyle = {
  padding: 'padding_8',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
};

const listItemStyle = {
  paddingTop: 'padding_4',
  paddingBottom: 'padding_4',
};

const clearAllStyle = { ...listItemStyle, cursor: 'pointer' };

const buttonAsSelectStyle = (hasSelections: boolean) => ({
  width: '100%',
  justifyContent: 'space-between',
  outline: '1px solid transparent',
  background: 'transparent',
  borderRadius: '4px',
  padding: 'padding_8',
  border: '1px solid',
  borderColor: hasSelections ? Colors.ForestGreen700 : Colors.ArmyGreen300,
  outlineColor: hasSelections ? Colors.ForestGreen700 : 'none',
  backgroundColor: Colors.White,
  '&:hover, &:focus': {
    borderColor: Colors.ForestGreen700,
    outlineColor: Colors.ForestGreen700,
    backgroundColor: Colors.White,
    boxShadow: 'none',
  },
});

type FilterOption = {
  key: string;
  label: string;
};

const searchContainerStyle = {};

const ITEM_HEIGHT = 32;
const DEFAULT_LIST_HEIGHT = 300;

const listContainerStyle = (height: number) => ({
  height: `${height}px`,
  overflowY: 'auto' as const,
});

type CheckboxItemProps = {
  optionKey: string;
  label: string;
  isChecked: boolean;
  onToggle: (key: string) => void;
};

const CheckboxItem = memo(
  ({ optionKey, label, isChecked, onToggle }: CheckboxItemProps) => (
    <ListItem sx={listItemStyle}>
      <Checkbox isChecked={isChecked} onToggle={() => onToggle(optionKey)}>
        <Text>{label}</Text>
      </Checkbox>
    </ListItem>
  ),
);

type Props = {
  label?: string;
  options: FilterOption[];
  onChangeSelection: (selectedKeys: string[]) => void;
  selectedKeys?: string[];
  placeholder?: string;
  forceOutline?: boolean;
  isFilterEnabled?: boolean;
  /** Max height of the options list in px. Defaults to 300. */
  maxHeight?: number;
  sortSelectedFirst?: boolean;
  /**
   * Render a custom trigger instead of the default select-style button (e.g. an
   * icon). Receives the open state. When set, pair with `menuMinWidth` so the
   * popover isn't constrained to the (often small) trigger width.
   */
  renderTrigger?: (isOpen: boolean) => ReactNode;
  /** Min width of the options popover in px. Useful with `renderTrigger`. */
  menuMinWidth?: number;
  /** Called when the options popover closes (e.g. to persist the selection). */
  onClose?: () => void;
  /** Popover placement relative to the trigger. Defaults to 'bottom-start'. */
  placement?: PopoverProps['placement'];
  /**
   * Bulk action shown at the top of the list: 'clear' deselects everything
   * (default), 'selectAll' selects every option.
   */
  bulkAction?: 'clear' | 'selectAll';
};

const MultiSelect = ({
  label,
  options,
  onChangeSelection,
  selectedKeys = [],
  placeholder = 'All',
  forceOutline = false,
  isFilterEnabled = false,
  maxHeight,
  sortSelectedFirst = false,
  renderTrigger,
  menuMinWidth,
  onClose,
  placement = 'bottom-start',
  bulkAction = 'clear',
}: Props) => {
  const [isSelectorOpen, setIsSelectorOpen] = useState<boolean>();
  const [searchQuery, setSearchQuery] = useState('');
  const listRef = useRef<HTMLUListElement>(null);

  const selectedKeysSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  // Ref pattern keeps the callback stable so memoized rows don't re-render en masse
  const selectedKeysRef = useRef(selectedKeys);
  selectedKeysRef.current = selectedKeys;

  const handleOptionToggle = useCallback(
    (key: string) => {
      const { current } = selectedKeysRef;
      const newSelectedList = current.includes(key)
        ? current.filter((k) => k !== key)
        : [...current, key];
      onChangeSelection(newSelectedList);
    },
    [onChangeSelection],
  );

  const handleBulkAction = useCallback(() => {
    if (bulkAction === 'selectAll') {
      onChangeSelection(options.map((o) => o.key));
    } else {
      onChangeSelection([]);
    }
  }, [bulkAction, onChangeSelection, options]);

  const visibleOptions = useMemo(() => {
    let filtered = options;

    if (isFilterEnabled && searchQuery) {
      filtered = options.filter((o) =>
        o.label.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    if (sortSelectedFirst) {
      const selected = filtered.filter((o) => selectedKeysSet.has(o.key));
      const unselected = filtered.filter((o) => !selectedKeysSet.has(o.key));

      return [...selected, ...unselected];
    }

    return filtered;
  }, [options, searchQuery, isFilterEnabled, sortSelectedFirst, selectedKeysSet]);

  const selectLabel = useMemo(() => {
    if (!selectedKeys.length || selectedKeys.length === options.length) {
      if (!label) {
        return placeholder;
      }

      return `${label}: ${placeholder}`;
    }

    if (!label) {
      return `${selectedKeys.length} selected`;
    }

    return `${label}: ${selectedKeys.length} selected`;
  }, [selectedKeys, options, label, placeholder]);

  const listHeight = Math.min(
    maxHeight ?? DEFAULT_LIST_HEIGHT,
    visibleOptions.length * ITEM_HEIGHT,
  );

  const virtualizer = useVirtualizer({
    count: visibleOptions.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 5,
  });

  const buttonStyle = useMemo(() => {
    if (!forceOutline || selectedKeys.length === 0) {
      return buttonAsSelectStyle(false);
    }

    return buttonAsSelectStyle(true);
  }, [selectedKeys, forceOutline]);

  return (
    <Box>
      <PopoverMenu
        placement={placement}
        matchWidth={!renderTrigger}
        onOpen={() => setIsSelectorOpen(true)}
        onClose={() => {
          setIsSelectorOpen(false);
          setSearchQuery('');
          onClose?.();
        }}
      >
        <PopoverMenu.Trigger>
          {renderTrigger ? (
            renderTrigger(!!isSelectorOpen)
          ) : (
            <Button
              variant="unstyled"
              sx={buttonStyle}
              endAdornment={
                isSelectorOpen ? (
                  <Icon name={Icon.Name.ChevronUp} />
                ) : (
                  <Icon name={Icon.Name.ChevronDown} />
                )
              }
            >
              {selectLabel}
            </Button>
          )}
        </PopoverMenu.Trigger>
        <PopoverMenu.Content
          sx={{
            ...filterOptionsStyle,
            ...(menuMinWidth
              ? { width: `${menuMinWidth}px`, minWidth: `${menuMinWidth}px` }
              : {}),
          }}
        >
          {isFilterEnabled && (
            <Box sx={searchContainerStyle}>
              <SearchInput value={searchQuery} onChange={setSearchQuery} />
            </Box>
          )}
          <Button variant="unstyled" sx={clearAllStyle} onClick={handleBulkAction}>
            <Text>{bulkAction === 'selectAll' ? 'Select All' : 'Clear All'}</Text>
          </Button>
          <List ref={listRef} sx={listContainerStyle(listHeight)}>
            <Box style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
              {virtualizer.getVirtualItems().map((virtualItem) => {
                const { key, label: optionLabel } = visibleOptions[virtualItem.index];

                return (
                  <Box
                    key={`checkbox-filter-${key}`}
                    style={{
                      position: 'absolute',
                      top: virtualItem.start,
                      left: 0,
                      width: '100%',
                      height: `${virtualItem.size}px`,
                    }}
                  >
                    <CheckboxItem
                      optionKey={key}
                      label={optionLabel}
                      isChecked={selectedKeysSet.has(key)}
                      onToggle={handleOptionToggle}
                    />
                  </Box>
                );
              })}
            </Box>
          </List>
        </PopoverMenu.Content>
      </PopoverMenu>
    </Box>
  );
};

export default MultiSelect;
